/**
 * packages/dados/src/repositorios.ts — as operações de leitura e escrita do Fincast.
 *
 * ONDE ESTE ARQUIVO SE ENCAIXA
 *   cliente.ts    → conexão com o Supabase (`obterCliente`, `ErroFincast`)
 *   sessao.ts     → quem é o usuário, o que ele pode, entrar/sair, aceitar convite
 *   conexao.ts    → estado da rede e do salvamento (a barra de status)
 *   mapeamento.ts → banco ⇄ motor (`paraMotor` / `paraBanco`)
 *   **repositorios.ts** → as consultas e as gravações. É este arquivo.
 *   rascunho.ts   → o salvamento automático do que o usuário está digitando
 *
 * PRINCÍPIOS
 *  1. Nada aqui decide permissão. Quem autoriza é a RLS (`db/migrations/0007_rls.sql`).
 *     O repositório pede; se o banco negar, o erro vira frase em português.
 *  2. `empresa_id` nunca é palavra da tela: vem do registro lido sob RLS, e a FK composta
 *     `(cenario_id, empresa_id) → cenario(id, empresa_id)` recusa qualquer par inventado.
 *  3. Escrita privilegiada só pelas RPCs de `0010_rpc.sql`. Nada de reimplementar aqui
 *     regra que já existe em `salvar_cenario`, `publicar_versao` ou `clonar_cenario`.
 *  4. O digitar do usuário NÃO passa por aqui. Ele vive em `rascunho.ts`. Este arquivo só
 *     PUBLICA — é o botão "Salvar".
 *
 * ⚠ PUBLICAR SÃO DUAS CHAMADAS, E ISSO É UMA LIMITAÇÃO REAL
 *   O banco não tem uma função que grave as tabelas E crie a versão. Tem duas:
 *     · `app.salvar_cenario`  → `app.cenario` + as 7 filhas, UMA transação, trava
 *                               otimista. Atômica.
 *     · `app.publicar_versao` → snapshot + linha em `app.cenario_versao`. Atômica.
 *   `publicarCenario()` chama as duas em ordem. Cada uma é atômica; o PAR não é. Se a
 *   segunda falhar, os dados ESTÃO salvos e a versão não foi registrada — estado
 *   consistente e recuperável: o erro é `VERSAO_NAO_REGISTRADA` e a correção é
 *   `registrarVersao()`, NÃO salvar de novo (bateria na trava otimista).
 *   Fechar isso exige uma RPC nova (`app.publicar_cenario`) — fora deste lote.
 */

import { ErroFincast, obterCliente } from './cliente';
import { conexao, ehFalhaDeConexao } from './conexao';
import {
  acessoDePlataforma, aoMudarAutenticacao, invalidarPerfil, obterPerfil, registrarAcessoAdmin,
} from './sessao';
import type { Papel } from './sessao';
import { paraBanco, paraMotor } from './mapeamento';
import type { EntradaMotor, LinhaEmpreendimento, LinhasBanco, OpcoesParaBanco } from './mapeamento';
import type { Database, Json } from './banco.tipos';
/* O motor é a fonte do formato de `PremissasMacro`: quem lê o banco tem de entregar
   exatamente o que `calcBase` consome, e é o compilador que garante isso. `Rating` e
   `Funding` NÃO vêm daqui — vêm do enum do banco, e a igualdade entre os dois é checada
   logo abaixo. */
import type { PremissasMacro as PremissasMotor, Funding as FundingMotor, Rating as RatingMotor } from '../../../engine/index';
import type { TaxasMensais as TaxasMotor } from '../../../engine/index';

/* =============================================================================
 * Tipos de fronteira
 * ========================================================================== */

type Tabelas = Database['app']['Tables'];
type Linha<T extends keyof Tabelas> = Tabelas[T]['Row'];

export type Programa = Database['app']['Enums']['programa'];
export type Rating = Database['app']['Enums']['rating'];
export type Funding = Database['app']['Enums']['funding'];
export type PremissasMacro = PremissasMotor;

/* Guarda de compilação: se alguém acrescentar um rating ou um funding no banco e esquecer
   do motor (ou o contrário), a linha abaixo para de compilar. Sem ela, o descompasso só
   apareceria como faixa de LGC faltando na tela, muito depois. */
type Identicos<A, B> = [A] extends [B] ? ([B] extends [A] ? true : never) : never;
const _ratingCasa: Identicos<Rating, RatingMotor> = true;
const _fundingCasa: Identicos<Funding, FundingMotor> = true;
void _ratingCasa;
void _fundingCasa;
export type { Papel };

/** Papéis em ordem de poder. `admin_hype` não é papel de empresa: é atributo do usuário. */
export const PAPEIS: readonly Papel[] = ['gestor', 'analista', 'consulta'] as const;

export interface Empresa {
  id: string;
  nome: string;
  cnpj: string | null;
  ativa: boolean;
  /**
   * `null` = enxerga por ser Admin Fincast (`is_admin_hype`), sem vínculo. Desde a 0031 (decisão
   * de 12/10/2026) o Admin escreve em toda incorporadora ATIVA, com ou sem vínculo — quem decide é
   * o banco (`emp_escrita`/`emp_admin`), não este campo.
   */
  meuPapel: Papel | null;
  souMembro: boolean;
  /** Quantos empreendimentos ATIVOS ela tem — a mesma RLS da lista, sem 2ª ida ao servidor. */
  empreendimentos: number;
}

/**
 * O cartão do empreendimento: o nome e quantos cenários ATIVOS ele tem. Sem números do cadastro —
 * desde a 0032 o cadastro (programa, VGV, unidades…) é de cada cenário, e o cartão não escolhe um.
 */
export interface EmpreendimentoResumo {
  id: string;
  empresaId: string;
  nome: string;
  criadoEm: string;
  /** cenários ATIVOS (o arquivado não conta, como no limite de 5) */
  cenarios: number;
}

/** Criar empreendimento grava só o nome (0032): o cadastro nasce no 1º cenário. */
export interface NovoEmpreendimento {
  nome: string;
}

/**
 * O cadastro de um cenário, nas colunas de `app.cenario` (0032). Para `criarCenario` com valores
 * iniciais; sem ele, o cenário nasce EM BRANCO pelos defaults do banco (SBPE, rating A, zeros, LGC 0
 * e PJ limite vazio) — decisão de 12/10/2026.
 */
export type CadastroCenario = Pick<Linha<'cenario'>,
  | 'programa' | 'rating' | 'vgv' | 'unidades' | 'cto_laudo' | 'desp_fin' | 'desp_com' | 'desp_seg'
  | 'desp_leg' | 'valor_terreno' | 'permutadas' | 'lgc_disponivel' | 'pj_limite'>;

export interface CenarioResumo {
  id: string;
  empresaId: string;
  empreendimentoId: string;
  nome: string;
  temFluxo: boolean;
  linhaCreditoId: number;
  /** Trava otimista: sobe a cada `salvar_cenario`. */
  versao: number;
  /** 0 = nunca publicado. */
  versaoPublicada: number;
  arquivadoEm: string | null;
  criadoEm: string;
}

export interface CenarioAberto {
  cenario: CenarioResumo;
  /** `{empreendimento, lev}` prontos para o motor. */
  entrada: EntradaMotor;
}

export interface ResultadoPublicacao {
  /** Nova versão de EDIÇÃO — a que a próxima publicação tem de informar. */
  versao: number;
  /** Número da versão PUBLICADA que acabou de nascer. */
  versaoPublicada: number;
  versaoId: string;
  criadoEm: string;
}

export interface Membro {
  usuarioId: string;
  nome: string;
  email: string;
  papel: Papel;
  ativo: boolean;
  desde: string;
}

export interface ConvitePendente {
  id: string;
  empresaId: string;
  email: string;
  papel: Papel;
  expiraEm: string;
  criadoEm: string;
  criadoPor: string;
}

export interface ConviteEmitido {
  convite: ConvitePendente;
  /**
   * ⚠ O token em claro existe SÓ AQUI, uma vez. O banco guarda apenas `sha256(token)`.
   * Mande por e-mail e descarte: não logue, não persista, não coloque em URL de rastreio.
   */
  token: string;
}

export interface LinhaCredito {
  id: number;
  nome: string;
}

/* ---- pedido de exclusão (migration 0021) -----------------------------------
   "Excluir" no Fincast é ARQUIVAR, e é reversível. Gestor e analista pedem; só o
   Admin Hype decide. O pedido sobrevive ao arquivamento de propósito: depois que a
   incorporadora é desligada, ela some de todo mundo — o pedido é a única alça que
   sobra para desfazer. */
export type AlvoExclusao = 'empreendimento' | 'empresa';
export type EstadoPedido = 'pendente' | 'aprovado' | 'recusado' | 'cancelado' | 'desfeito';

export interface PedidoExclusao {
  id: string;
  empresaId: string;
  alvo: AlvoExclusao;
  empreendimentoId: string | null;
  /** Nome do alvo no momento do pedido: depois de arquivado, o alvo some do SELECT. */
  alvoNome: string;
  motivo: string;
  estado: EstadoPedido;
  solicitanteId: string;
  solicitanteNome: string;
  solicitadoEm: string;
  decididoEm: string | null;
  decisaoNota: string;
}

/* =============================================================================
 * Erros
 *
 * A classe é a do pacote (`ErroFincast`, de cliente.ts) — uma só para tudo. O que
 * este arquivo acrescenta é o dicionário de erros de DADOS (RLS, constraint, trava
 * otimista), que `sessao.ts` não cobre porque lá o assunto é autenticação.
 * ========================================================================== */

export type CodigoErro =
  | 'SEM_CONEXAO'
  | 'NAO_AUTENTICADO'
  | 'SEM_PERMISSAO'
  | 'CADASTRO_SEM_CONVITE'
  | 'CONVITE_INVALIDO'
  | 'CONVITE_DE_OUTRO_EMAIL'
  | 'CONVITE_SEM_EMISSOR'
  | 'CONVITE_PARA_SI'
  | 'CONVITE_DUPLICADO'
  | 'TENANT_IMUTAVEL'
  | 'CONFLITO_VERSAO'
  | 'VERSAO_NAO_REGISTRADA'
  | 'CENARIO_INEXISTENTE'
  | 'CENARIO_ARQUIVADO'
  | 'CENARIO_ATIVO'
  | 'EMPREENDIMENTO_ARQUIVADO'
  | 'SO_ADMIN_EXCLUI'
  | 'LIMITE_CENARIOS'
  | 'SEM_GESTOR'
  | 'EXTENSAO_SO_GESTOR'
  | 'EXTENSAO_SEM_AUTORIZACAO'
  | 'NOME_DUPLICADO'
  | 'NOME_OBRIGATORIO'
  | 'PEDIDO_DUPLICADO'
  | 'PEDIDO_JA_DECIDIDO'
  | 'SO_ADMIN_HYPE'
  | 'NOME_EM_USO'
  | 'NADA_A_DESFAZER'
  | 'NAO_ENCONTRADO'
  | 'DADO_INVALIDO'
  | 'REGRA_VIOLADA'
  | 'ERRO_INESPERADO'
  /* ---- cadastro por cenário (0032) ---- */
  | 'PACOTE_DESATUALIZADO'
  | 'CADASTRO_INVALIDO';

/** Contexto que viaja em `ErroFincast.causa` — para log, nunca para a tela. */
export interface CausaErro {
  operacao: string;
  detalhe: string;
  erro: unknown;
  dados?: Record<string, unknown>;
}

function erro(
  codigo: CodigoErro,
  mensagem: string,
  causa: CausaErro,
): ErroFincast {
  return new ErroFincast(codigo, mensagem, causa);
}

/** `true` quando repetir a mesma operação pode dar certo (queda de rede). */
export function ehErroDeConexao(e: unknown): boolean {
  return e instanceof ErroFincast ? e.codigo === 'SEM_CONEXAO' : ehFalhaDeConexao(e);
}

function campoTexto(o: Record<string, unknown>, chave: string): string {
  const v = o[chave];
  return typeof v === 'string' ? v : '';
}

function textoDoErro(e: unknown): string {
  if (typeof e === 'string') return e;
  if (e instanceof Error) return `${e.name}: ${e.message}`;
  if (e && typeof e === 'object') {
    const o = e as Record<string, unknown>;
    return [campoTexto(o, 'message'), campoTexto(o, 'details'), campoTexto(o, 'hint')]
      .filter((p) => p !== '')
      .join(' | ');
  }
  return String(e);
}

function codigoPg(e: unknown): string {
  if (e && typeof e === 'object') {
    const c = (e as Record<string, unknown>)['code'];
    if (typeof c === 'string') return c;
  }
  return '';
}

/**
 * Os campos do cadastro do cenário (0032) pelo NOME QUE A TELA MOSTRA — o das etiquetas do
 * simulador (`fld(...)` em simulador.js). Sem ID de elemento (regra da v65). É o que as frases de
 * erro do cadastro dizem, no cliente e na tradução do CHECK do banco.
 */
export const ROTULO_DO_CADASTRO = {
  vgv: 'VGV',
  unidades: 'Unidades totais',
  cto_laudo: 'CTO Laudo',
  desp_fin: 'Despesas financeiras',
  desp_com: 'Despesas comerciais',
  desp_seg: 'Despesas com seguros',
  desp_leg: 'Despesas de legalização',
  valor_terreno: 'Valor de avaliação do terreno',
  permutadas: 'Total de unidades permutadas/quitadas',
  lgc_disponivel: 'LGC disponível',
  pj_limite: 'PJ limite',
} as const;

const CADASTRO_INTEIRO: ReadonlySet<string> = new Set(['unidades', 'permutadas']);

/**
 * Antes de publicar (0032): o cadastro vai no `salvar_cenario`, e um valor que o banco recusa
 * (negativo, unidades não inteiras) reprovaria a publicação INTEIRA — antes, só a gravação do
 * cadastro. Esta conferência diz QUAIS campos, sem ir ao servidor. Devolve as frases (vazio = ok).
 * O PJ limite fica de fora: ≤ 0 já vira "sem limite" no mapeamento.
 */
export function problemasDoCadastro(cenario: Record<string, unknown>): string[] {
  const problemas: string[] = [];
  for (const [coluna, rotulo] of Object.entries(ROTULO_DO_CADASTRO)) {
    if (coluna === 'pj_limite') continue;
    const v = cenario[coluna];
    if (typeof v !== 'number' || !Number.isFinite(v)) continue;   // o mapeamento já zera o que não é número
    if (v < 0) problemas.push(`“${rotulo}” não pode ser negativo`);
    else if (CADASTRO_INTEIRO.has(coluna) && !Number.isInteger(v)) problemas.push(`“${rotulo}” precisa ser um número inteiro`);
  }
  return problemas;
}

/**
 * Converte qualquer erro (PostgrestError, falha de fetch, string) em `ErroFincast`
 * com frase de tela. A ordem importa: o texto do Postgres é mais específico que o
 * SQLSTATE, e a queda de rede vem antes de tudo — sem resposta não há SQLSTATE.
 */
export function traduzirErro(bruto: unknown, operacao: string): ErroFincast {
  if (bruto instanceof ErroFincast) return bruto;

  const cru = textoDoErro(bruto) || String(bruto);
  const codigo = codigoPg(bruto);
  const causa: CausaErro = { operacao, detalhe: cru, erro: bruto };

  // ehFalhaDeConexao é do conexao.ts: uma só definição de "caiu" no pacote inteiro.
  if (ehFalhaDeConexao(bruto)) {
    return erro(
      'SEM_CONEXAO',
      'Sem conexão com o servidor. O cálculo continua rodando aqui no navegador, mas nada está sendo salvo.',
      causa,
    );
  }

  /* ---- convite / cadastro ---- */
  if (cru.includes('CADASTRO_SEM_CONVITE')) {
    return erro(
      'CADASTRO_SEM_CONVITE',
      'Este e-mail não tem convite válido. A entrada no Fincast é só por convite — peça um ao gestor da sua incorporadora.',
      causa,
    );
  }
  if (cru.includes('CONVITE_DE_OUTRO_EMAIL')) {
    return erro(
      'CONVITE_DE_OUTRO_EMAIL',
      'Este convite foi emitido para outro e-mail. Entre com o e-mail que recebeu o convite.',
      causa,
    );
  }
  if (cru.includes('CONVITE_SEM_EMISSOR')) {
    return erro('CONVITE_SEM_EMISSOR', 'Quem emitiu este convite não tem mais acesso à incorporadora. Peça um convite novo ao gestor.', causa);
  }
  /* 0031 (D21): o banco recusa convite para o próprio e-mail — e o aceite de convite que a
     pessoa mesma emitiu. Vem com 42501; sem esta linha cairia no "sem permissão" genérico. */
  if (cru.includes('CONVITE_PARA_SI')) {
    return erro(
      'CONVITE_PARA_SI',
      'Ninguém pode convidar o próprio e-mail. Peça a outro gestor ou administrador que envie o convite.',
      causa,
    );
  }
  if (cru.includes('CONVITE_INVALIDO')) {
    return erro('CONVITE_INVALIDO', 'Convite inválido, já usado ou vencido. Peça um convite novo ao gestor.', causa);
  }

  /* ---- pedido de exclusão ---- */
  if (cru.includes('PEDIDO_DUPLICADO')) {
    return erro('PEDIDO_DUPLICADO', 'Já existe um pedido de exclusão aguardando decisão para este item.', causa);
  }
  if (cru.includes('PEDIDO_JA_DECIDIDO')) {
    return erro('PEDIDO_JA_DECIDIDO', 'Este pedido já foi decidido. Recarregue a lista para ver como ficou.', causa);
  }
  if (cru.includes('SO_ADMIN_HYPE')) {
    return erro('SO_ADMIN_HYPE', 'Só o administrador da plataforma decide pedidos de exclusão.', causa);
  }
  if (cru.includes('NOME_EM_USO')) {
    return erro(
      'NOME_EM_USO',
      'Já existe um empreendimento ativo com este nome nesta incorporadora. Renomeie o outro antes de restaurar este.',
      causa,
    );
  }
  if (cru.includes('NADA_A_DESFAZER')) {
    return erro('NADA_A_DESFAZER', 'Este pedido não está aprovado — não há arquivamento para desfazer.', causa);
  }

  /* ---- arquivar / restaurar / excluir de vez um cenário (0028) ---- */
  if (cru.includes('SO_ADMIN_EXCLUI')) {
    return erro('SO_ADMIN_EXCLUI', 'Só o administrador da plataforma exclui um cenário definitivamente.', causa);
  }
  if (cru.includes('CENARIO_ATIVO')) {
    return erro('CENARIO_ATIVO', 'Arquive o cenário antes de excluí-lo definitivamente.', causa);
  }
  if (cru.includes('EMPREENDIMENTO_ARQUIVADO')) {
    return erro(
      'EMPREENDIMENTO_ARQUIVADO',
      'O empreendimento deste cenário está arquivado. Ele precisa ser restaurado antes.',
      causa,
    );
  }
  if (cru.includes('NOME_CENARIO_EM_USO')) {
    return erro(
      'NOME_DUPLICADO',
      'Já existe um cenário ativo com este nome neste empreendimento. Renomeie o outro antes de restaurar este.',
      causa,
    );
  }

  /* ---- cadastro por cenário (0032) ----
     A trava do `salvar_cenario` recusa o bloco "cenario" sem `lgc_disponivel`: é o pacote de
     ANTES da 0032 (aba aberta antes da publicação). Nada foi gravado; depois do F5 o pacote novo
     restaura o rascunho e publica certo. */
  if (cru.includes('PACOTE_DESATUALIZADO')) {
    return erro(
      'PACOTE_DESATUALIZADO',
      'O Fincast foi atualizado. Recarregue a página (F5) — o que você digitou está guardado.',
      causa,
    );
  }

  /* ---- tenant ---- */
  if (cru.includes('TENANT_IMUTAVEL')) {
    return erro(
      'TENANT_IMUTAVEL',
      'Um registro não pode mudar de incorporadora. Crie o registro na incorporadora certa.',
      causa,
    );
  }

  /* ---- RLS: o erro que o usuário mais vai ver ao pisar fora do tenant ---- */
  if (/row-level security|row level security/i.test(cru)) {
    return erro('SEM_PERMISSAO', 'Você não tem permissão nesta incorporadora.', causa);
  }
  if (/permission denied for (table|column|schema|function)/i.test(cru)) {
    return erro('SEM_PERMISSAO', 'Seu perfil não permite esta alteração nesta incorporadora.', causa);
  }

  /* ---- versão / cenário ---- */
  if (cru.includes('CONFLITO_VERSAO') || codigo === '40001') {
    return erro(
      'CONFLITO_VERSAO',
      'Alguém publicou este cenário enquanto você editava. Recarregue para ver a versão nova antes de salvar — seu rascunho continua aqui.',
      causa,
    );
  }
  if (cru.includes('CENARIO_ARQUIVADO')) {
    return erro('CENARIO_ARQUIVADO', 'Este cenário está arquivado e não aceita alteração.', causa);
  }
  if (cru.includes('CENARIO_INEXISTENTE')) {
    return erro('CENARIO_INEXISTENTE', 'Cenário não encontrado — ou você não tem permissão para vê-lo.', causa);
  }
  if (cru.includes('VERSAO_IMUTAVEL')) {
    return erro('REGRA_VIOLADA', 'Versão publicada não pode ser alterada. Publique uma versão nova.', causa);
  }
  if (cru.includes('LIMITE_CENARIOS')) {
    return erro(
      'LIMITE_CENARIOS',
      /* Desde 09/10/2026 arquivar existe na tela (o seletor do cenário) e libera a vaga na
         hora — a frase vale para criar, copiar e restaurar. */
      'Este empreendimento já tem 5 cenários ativos. Arquive um para liberar a vaga.',
      causa,
    );
  }
  if (cru.includes('EXTENSAO_SO_GESTOR')) {
    return erro('EXTENSAO_SO_GESTOR', 'Só um gestor pode autorizar extensão de prazo da demanda.', causa);
  }
  if (cru.includes('NOME_OBRIGATORIO')) {
    return erro('NOME_OBRIGATORIO', 'Informe um nome.', causa);
  }

  /* ---- autenticação ---- */
  if (
    cru.includes('NAO_AUTENTICADO') ||
    cru.includes('SEM_EMAIL_NO_TOKEN') ||
    codigo === 'PGRST301' ||
    /jwt (expired|invalid)|invalid claim|missing sub claim/i.test(cru)
  ) {
    return erro('NAO_AUTENTICADO', 'Sua sessão expirou. Entre de novo para continuar.', causa);
  }
  if (cru.includes('SEM_PERMISSAO') || cru.includes('SEM_ACESSO')) {
    return erro('SEM_PERMISSAO', 'Você não tem permissão nesta incorporadora.', causa);
  }
  if (cru.includes('Alteracao de papel/status/e-mail nao permitida')) {
    return erro('SEM_PERMISSAO', 'Ninguém altera o próprio papel, status ou e-mail. Peça a um gestor.', causa);
  }

  /* ---- constraints ---- */
  if (codigo === '23505' || /duplicate key value/i.test(cru)) {
    if (/convite_pendente_uk/.test(cru)) {
      return erro(
        'CONVITE_DUPLICADO',
        'Já existe um convite pendente para este e-mail nesta incorporadora. Revogue o antigo antes de emitir outro.',
        causa,
      );
    }
    if (/empreendimento_nome_uk/.test(cru)) {
      return erro('NOME_DUPLICADO', 'Já existe um empreendimento com este nome nesta incorporadora.', causa);
    }
    /* 0028: dois cenários ATIVOS com o mesmo nome ficariam indistinguíveis no seletor. Vale
       para criar, copiar e renomear (restaurar tem frase própria, acima). */
    if (/cenario_nome_ativo_uk/.test(cru)) {
      return erro('NOME_DUPLICADO', 'Já existe um cenário ativo com este nome neste empreendimento. Escolha outro nome.', causa);
    }
    return erro('NOME_DUPLICADO', 'Já existe um registro com estes dados.', causa);
  }
  if (codigo === '23514' || /violates check constraint/i.test(cru)) {
    if (/sem gestor ativo/i.test(cru)) {
      return erro('SEM_GESTOR', 'A incorporadora ficaria sem nenhum gestor ativo. Promova outra pessoa antes.', causa);
    }
    /* A mensagem genérica manda "revise os campos" sem dizer qual — e no caso da
       extensão de demanda o campo violado não é nem o que a pessoa editou. */
    if (/cen_extensao_autor/.test(cru)) {
      return erro(
        'EXTENSAO_SEM_AUTORIZACAO',
        'A extensão de prazo da demanda precisa ser autorizada por um gestor. Peça a autorização ou zere a extensão.',
        causa,
      );
    }
    /* 0032: cada CHECK do cadastro do cenário tem o nome da coluna — a frase diz QUAL campo. */
    const doCadastro = /cen_cad_([a-z_]+)_nao_negativo/.exec(cru);
    if (doCadastro !== null) {
      const rotulo = ROTULO_DO_CADASTRO[doCadastro[1] as keyof typeof ROTULO_DO_CADASTRO] ?? doCadastro[1];
      return erro(
        'CADASTRO_INVALIDO',
        `O campo “${rotulo}” não aceita valor negativo. Corrija e publique de novo — o que você digitou está guardado.`,
        causa,
      );
    }
    return erro('REGRA_VIOLADA', 'Valor fora do que o cadastro aceita. Revise os campos.', causa);
  }
  if (codigo === '23503') {
    return erro('REGRA_VIOLADA', 'Há um registro dependente ou uma referência que não existe.', causa);
  }
  if (codigo === '22023' || codigo === '22P02' || cru.includes('CONTEUDO_INVALIDO')) {
    return erro('DADO_INVALIDO', 'Dado em formato inválido para o servidor.', causa);
  }
  if (codigo === '42501') {
    return erro('SEM_PERMISSAO', 'Você não tem permissão nesta incorporadora.', causa);
  }
  if (codigo === 'PGRST116' || codigo === 'P0002') {
    return erro('NAO_ENCONTRADO', 'Registro não encontrado — ou fora do seu acesso.', causa);
  }

  return erro(
    'ERRO_INESPERADO',
    'Não foi possível concluir a operação. Tente de novo; se continuar, avise o suporte.',
    causa,
  );
}

/* =============================================================================
 * Execução das consultas
 * ========================================================================== */

/* O tipo de resposta do Supabase é uma UNIÃO: `{data:T;error:null}` no sucesso e
   `{data:null;error:PostgrestError}` na falha. Declarar o parâmetro como
   `Resposta<T>` fazia o TypeScript casar com o ramo do erro e inferir `T = null`
   — por isso `exec` prometia `Promise<null>` e todo chamador quebrava.
   A saída é capturar a resposta INTEIRA em `R` e derivar o dado de `R['data']`. */
type Resposta = { data: unknown; error: unknown };

/**
 * Executa, alimenta o monitor de conexão (uma leitura que falha também é prova de que
 * a rede caiu) e devolve o dado. `null` vira `NAO_ENCONTRADO`.
 */
async function exec<R extends Resposta>(
  consulta: PromiseLike<R>,
  operacao: string,
): Promise<NonNullable<R['data']>> {
  const dado = await execOpcional(consulta, operacao);
  if (dado === null || dado === undefined) {
    throw erro('NAO_ENCONTRADO', 'Registro não encontrado — ou fora do seu acesso.', {
      operacao,
      detalhe: 'resposta sem dado',
      erro: null,
    });
  }
  /* O TypeScript não estreita `R['data']` genérico com um `if`; a checagem acima
     já garantiu que não é nulo. */
  return dado as NonNullable<R['data']>;
}

/** Igual, mas aceita `null` (RPC `void`, `maybeSingle`). */
async function execOpcional<R extends Resposta>(consulta: PromiseLike<R>, operacao: string): Promise<R['data']> {
  let r: R;
  try {
    r = await consulta;
  } catch (e) {
    conexao.registrarFalha(e);
    throw traduzirErro(e, operacao);
  }
  if (r.error !== null && r.error !== undefined) {
    conexao.registrarFalha(r.error);
    throw traduzirErro(r.error, operacao);
  }
  conexao.registrarSucesso();
  return r.data;
}

/** O cliente já nasce apontado para o schema `app` (cliente.ts, `db: { schema: 'app' }`). */
function app() {
  return obterCliente();
}

function exigirTexto(valor: string, campo: string): string {
  const v = valor.trim();
  if (v === '') {
    throw erro('NOME_OBRIGATORIO', `Informe ${campo}.`, { operacao: campo, detalhe: 'vazio', erro: null });
  }
  return v;
}

async function idDoUsuario(operacao: string): Promise<string> {
  const perfil = await obterPerfil();
  if (perfil === null) {
    throw erro('NAO_AUTENTICADO', 'Você precisa entrar para usar o Fincast.', {
      operacao,
      detalhe: 'sem perfil',
      erro: null,
    });
  }
  return perfil.id;
}

/* =============================================================================
 * Registro de acesso do Admin Hype
 *
 * A função do banco (`0017`) já ignora quem não é Admin Hype, quem é membro da
 * empresa e a repetição dentro de uma hora. O que evitamos aqui é a ida ao servidor:
 * uma vez por empresa por sessão basta.
 * ========================================================================== */

/**
 * Chaveado por `(ator, empresa)`, não só por empresa.
 *
 * Com a chave só de empresa, dois Admins Hype no mesmo navegador se anulavam: o 2º ator
 * acessava dados de uma incorporadora já "registrada" pelo 1º e NÃO deixava rastro,
 * embora a dedup do banco seja por `(ator_id, empresa_id, 1 hora)` e fosse gravar. Quem
 * suprimia o registro era o cliente — e o cliente não é a autoridade sobre a trilha.
 */
const acessosJaRegistrados = new Set<string>();
const chaveAcesso = (atorId: string, empresaId: string): string => `${atorId}|${empresaId}`;

/** Esquece o que já foi registrado. Chamado por `sair()` e a cada mudança de autenticação. */
export function limparAcessosRegistrados(): void {
  acessosJaRegistrados.clear();
}

/* `limparAcessosRegistrados` não tinha UM chamador em todo o repositório, apesar de o
   docstring mandar chamá-la ao trocar de usuário. Ligar por aqui, na primeira vez que o
   registro é usado, resolve sem criar ciclo de import: `sessao.ts` não pode importar
   este arquivo (é ele quem é importado), e registrar no carregamento do módulo falharia
   porque `aoMudarAutenticacao` precisa do cliente já criado. */
let ouvindoTrocaDeUsuario = false;
function garantirResetPorAutenticacao(): void {
  if (ouvindoTrocaDeUsuario) return;
  ouvindoTrocaDeUsuario = true;
  try {
    aoMudarAutenticacao(() => limparAcessosRegistrados());
  } catch {
    ouvindoTrocaDeUsuario = false; // cliente ainda não criado: tenta na próxima
  }
}

async function registrarAcessoSeForDeFora(empresaId: string, motivo: string): Promise<void> {
  garantirResetPorAutenticacao();
  const perfil = await obterPerfil();
  if (perfil === null || !acessoDePlataforma(perfil, empresaId)) return;

  /* O perfil vem ANTES da consulta ao conjunto: sem o ator não há chave, e era
     justamente a ausência do ator na chave que perdia o rastro do 2º admin. */
  const chave = chaveAcesso(perfil.id, empresaId);
  if (acessosJaRegistrados.has(chave)) return;

  acessosJaRegistrados.add(chave);
  try {
    await registrarAcessoAdmin(empresaId, motivo);
  } catch (e) {
    // Governança, não controle de acesso: falhar aqui não pode fechar a tela do
    // suporte. Reabre para tentar de novo na próxima navegação.
    acessosJaRegistrados.delete(chave);
    if (!ehErroDeConexao(e)) throw traduzirErro(e, 'registrar acesso');
  }
}

/* =============================================================================
 * Empresas (incorporadoras)
 * ========================================================================== */

/* `app.empresa.lgc_disponivel` está CONGELADA desde a 0032 (o LGC é de cada cenário): sem SELECT
   e sem UPDATE para `authenticated` — pedir por ela falharia a lista inteira; gravar, o editar.
   A coluna continua em `banco.tipos.ts` (o gerador lê colunas, não grants: tirá-la quebraria a
   "diferença zero" depois de aplicar). Por isso o app usa estes dois tipos, e não os da tabela crua:
   um uso novo do LGC da incorporadora não compila. Saem (com a coluna) na 0033. */
type ColunaCongeladaDaEmpresa = 'lgc_disponivel';
export type LinhaEmpresa = Omit<Linha<'empresa'>, ColunaCongeladaDaEmpresa>;
export type PatchEmpresa = Omit<Tabelas['empresa']['Update'], ColunaCongeladaDaEmpresa>;

type EmpresaBreve = Pick<LinhaEmpresa, 'id' | 'nome' | 'cnpj' | 'ativa'> & {
  /* Recurso EMBUTIDO do PostgREST, não uma 2ª chamada. A RLS do embutido é a mesma
     `empd_sel`, então arquivado já sai de fora e nada vaza de outra incorporadora.
     Contar chamando `listarEmpreendimentos` por empresa seria pior de dois jeitos: N
     idas ao servidor, e um registro de acesso de plataforma em TODAS elas na trilha
     de auditoria — justamente o que o cliente vê. */
  empreendimento: { id: string }[] | null;
};

/**
 * Empresas que o usuário enxerga, com CNPJ. Quem decide a visibilidade é a RLS
 * (`emp_leitura()`); o papel vem do perfil, sem recalcular a regra de vínculo aqui.
 */
export async function listarEmpresas(): Promise<Empresa[]> {
  const [linhas, perfil] = await Promise.all([
    exec(
      app().from('empresa').select('id, nome, cnpj, ativa, empreendimento(id)').order('nome') as unknown as
        PromiseLike<{ data: EmpresaBreve[] | null; error: unknown }>,
      'empresas',
    ),
    obterPerfil(),
  ]);

  const vinculo = new Map((perfil?.empresas ?? []).map((v) => [v.empresaId, v.papel]));
  return linhas.map((e: EmpresaBreve) => ({
    id: e.id,
    nome: e.nome,
    cnpj: e.cnpj,
    ativa: e.ativa,
    meuPapel: vinculo.get(e.id) ?? null,
    souMembro: (vinculo.get(e.id) ?? null) !== null,
    empreendimentos: e.empreendimento?.length ?? 0,
  }));
}

/**
 * Edita o cadastro da incorporadora. Quem pode é decidido pela RLS (`empresa_upd`:
 * Admin Hype ou gestor); o que pode ser escrito, pelo grant de coluna — `ativa` fica
 * de fora de propósito, porque desligar a incorporadora é o arquivamento, e esse só
 * o Admin Hype faz, por RPC.
 */
export async function atualizarEmpresa(
  empresaId: string,
  dados: { nome?: string; cnpj?: string | null },
): Promise<Empresa> {
  const set: PatchEmpresa = {};
  if (dados.nome !== undefined) set.nome = exigirTexto(dados.nome, 'o nome da incorporadora');
  if (dados.cnpj !== undefined) {
    /* Mesma normalização de `criarEmpresa`: guarda 14 dígitos, aceita máscara. Vazio
       limpa o campo — não é o mesmo que "não mexer", que é `undefined`. */
    const so = (dados.cnpj ?? '').replace(/\D/g, '');
    if (so === '') set.cnpj = null;
    else if (so.length !== 14) {
      throw erro('DADO_INVALIDO', 'O CNPJ precisa ter 14 dígitos.', {
        operacao: 'atualizar incorporadora',
        detalhe: `cnpj com ${so.length} dígitos`,
        erro: null,
      });
    } else set.cnpj = so;
  }
  if (Object.keys(set).length === 0) {
    const atuais = await listarEmpresas();
    const achada = atuais.find((e) => e.id === empresaId);
    if (achada === undefined) {
      throw erro('NAO_ENCONTRADO', 'Incorporadora não encontrada — ou fora do seu acesso.', {
        operacao: 'atualizar incorporadora',
        detalhe: 'nada a mudar e id não encontrado',
        erro: null,
        dados: { empresaId },
      });
    }
    return achada;
  }

  await exec(
    app().from('empresa').update(set).eq('id', empresaId).select('id').single(),
    'atualizar incorporadora',
  );
  /* Relê pela lista: é ela que sabe juntar papel e contagem, e duplicar essa junção
     aqui seria a segunda fonte da verdade sobre o que é uma Empresa. */
  const depois = await listarEmpresas();
  const achada = depois.find((e) => e.id === empresaId);
  if (achada === undefined) {
    throw erro('NAO_ENCONTRADO', 'Incorporadora não encontrada depois de salvar.', {
      operacao: 'atualizar incorporadora',
      detalhe: 'sumiu da lista após update',
      erro: null,
      dados: { empresaId },
    });
  }
  return achada;
}

export interface NovaEmpresa {
  nome: string;
  /** Só dígitos, 14 posições. `undefined`/vazio = não informado (a coluna é única). */
  cnpj?: string;
}

/**
 * Cria uma incorporadora. **Só o Admin Fincast consegue** — `empresa_ins` exige
 * `app.eh_admin_hype()`. Quem cria NÃO vira membro: a incorporadora nasce sem ninguém
 * dentro e ganha o primeiro gestor por convite. (Desde a 0031 — decisão de 12/10/2026 — o
 * Admin escreve em toda incorporadora ATIVA sem precisar de vínculo; o vínculo é da equipe
 * do cliente.) O LGC não é mais dado da incorporadora: é de cada cenário (0032).
 *
 * ⚠ O `.select()` depois do `.insert()` não é enfeite: sem ele o cliente não devolve a
 * linha criada. Foi exatamente aqui que faltou política de SELECT para o Admin Hype
 * (corrigido em `0015_admin_ve_empresas.sql`) — criar empresa falhava com mensagem de
 * RLS enganosa, como se o INSERT tivesse sido negado.
 */
export async function criarEmpresa(dados: NovaEmpresa): Promise<Empresa> {
  const uid = await idDoUsuario('criar incorporadora');
  const nome = exigirTexto(dados.nome, 'o nome da incorporadora');

  const cnpj = (dados.cnpj ?? '').replace(/\D/g, '');
  if (cnpj !== '' && cnpj.length !== 14) {
    throw erro('REGRA_VIOLADA', 'O CNPJ precisa ter 14 dígitos.', {
      operacao: 'criar incorporadora',
      detalhe: `cnpj com ${cnpj.length} dígitos`,
      erro: null,
    });
  }

  const linha = await exec(
    app()
      .from('empresa')
      .insert({
        nome,
        cnpj: cnpj === '' ? null : cnpj,
        criada_por: uid,
      })
      .select('id, nome, cnpj, ativa')
      .single(),
    'criar incorporadora',
  );

  invalidarPerfil(); // a lista de empresas do Admin Hype acabou de mudar
  return {
    id: linha.id,
    nome: linha.nome,
    cnpj: linha.cnpj,
    ativa: linha.ativa,
    meuPapel: null,
    souMembro: false,
    empreendimentos: 0,   // acabou de nascer
  };
}

/* =============================================================================
 * Premissas macro (a global `PREMISSAS` do protótipo)
 *
 * Três tabelas, mesma convenção: `empresa_id is null` é o padrão da Hype e uma linha com
 * `empresa_id` preenchido sobrescreve. A RLS já entrega só o padrão + o que é do usuário,
 * então resolver aqui é escolher entre duas linhas, nunca decidir permissão.
 *
 * ⚠ Só `desp` entra em cálculo (as faixas de aviso de `calcBase`). `lgc` não tem chamador
 * desde a v1 do protótipo e `perfil` alimenta a sugestão de % de financiamento na tela.
 * ========================================================================== */

const FUNDINGS: readonly Funding[] = ['FGTS', 'SBPE', 'Mercado'] as const;
const RATINGS: readonly Rating[] = ['AA', 'A', 'B', 'C', 'D'] as const;

type LinhaDesp = Linha<'premissa_despesa'>;
type LinhaLgc = Linha<'premissa_lgc'>;
type LinhaPerfil = Linha<'premissa_perfil'>;

/** Entre a linha da incorporadora e a da Hype, a da incorporadora ganha. */
function preferirDaEmpresa<T extends { empresa_id: string | null }>(
  linhas: T[],
  empresaId: string,
): T | undefined {
  return linhas.find((l) => l.empresa_id === empresaId) ?? linhas.find((l) => l.empresa_id === null);
}

/**
 * As séries de juros do Fluxo mensal — Fluxo pelo memorial (15/10/2026), migration 0034: o realizado
 * do Banco Central (`app.ref_indice_mensal`, gravado por tools/db/atualizar-indices.mjs) e a projeção
 * (`app.premissa_taxa_projecao`), no formato `TaxasMensais` do motor.
 *
 * NUNCA lança: num banco ANTES da 0034 as tabelas não existem, e uma tela de simulação inteira caída
 * por causa da linha de juros seria desproporcional. Sem taxas, devolve `null` — o motor deixa a linha
 * "Despesas Financeiras" com o aviso, em vez de inventar número.
 */
export async function carregarTaxas(): Promise<TaxasMotor | null> {
  try {
    /* revisão do PR SAAS-984, comentário 7 (20/10/2026): o PostgREST devolve no máximo 1.000 linhas por consulta — sem
       paginar, uma carga mais longa (o `--desde` antigo do db:indices) perderia meses em silêncio. Lê página a página,
       em ordem, até vir uma página incompleta */
    const POR_PAGINA = 1000;
    const todas = async <T,>(pagina: (de: number, ate: number) => PromiseLike<{ data: T[] | null; error: unknown }>) => {
      const linhas: T[] = [];
      for (let de = 0; ; de += POR_PAGINA) {
        const r = await pagina(de, de + POR_PAGINA - 1);
        if (r.error) return { data: null, error: r.error };
        const d = r.data ?? [];
        linhas.push(...d);
        if (d.length < POR_PAGINA) return { data: linhas, error: null };
      }
    };
    const [real, proj] = await Promise.all([
      todas((de, ate) => app().from('ref_indice_mensal').select('indexador, mes, valor').order('indexador').order('mes').range(de, ate)),
      todas((de, ate) => app().from('premissa_taxa_projecao').select('indexador, desde, valor').order('indexador').order('desde').range(de, ate)),
    ]);
    if (real.error || proj.error) return null;
    const ymDe = (d: string) => { const [a, m] = d.split('-').map(Number); return (a as number) * 12 + ((m as number) - 1); };
    const campo = { cdi: 'cdiMes', poupanca: 'poupMes', tr: 'trMes' } as const;
    const realizado: TaxasMotor['realizado'] = {};
    for (const l of real.data ?? []) {
      const k = campo[l.indexador as keyof typeof campo];
      if (k) (realizado[ymDe(l.mes)] ??= {})[k] = Number(l.valor);
    }
    const projecao = (proj.data ?? [])
      .filter((l) => l.indexador === 'cdi' || l.indexador === 'poupanca' || l.indexador === 'tr')
      .map((l) => ({ indexador: l.indexador as 'cdi' | 'poupanca' | 'tr', desde: ymDe(l.desde), valor: Number(l.valor) }));
    return { realizado, projecao };
  } catch {
    return null;
  }
}

export async function carregarPremissas(empresaId: string): Promise<PremissasMacro> {
  const [desp, lgc, perfil] = await Promise.all([
    exec(app().from('premissa_despesa').select('*'), 'premissas de despesa'),
    exec(app().from('premissa_lgc').select('*'), 'faixas de LGC'),
    exec(app().from('premissa_perfil').select('*').order('ordem'), 'perfil de financiamento'),
  ]);

  const d = preferirDaEmpresa(desp as LinhaDesp[], empresaId);
  if (d === undefined) {
    throw erro('NAO_ENCONTRADO', 'As premissas de despesa não foram encontradas.', {
      operacao: 'carregar premissas',
      detalhe: 'premissa_despesa sem linha padrão nem da incorporadora',
      erro: null,
    });
  }

  /* LGC resolve por PAR (rating, funding) — é o mesmo que a view `v_empreendimento_lgc`
     faz no banco, e permite a uma incorporadora sobrescrever só algumas células. */
  const porPar = new Map<string, LinhaLgc[]>();
  for (const l of lgc as LinhaLgc[]) {
    const chave = `${l.rating}|${l.funding}`;
    const lista = porPar.get(chave);
    if (lista === undefined) porPar.set(chave, [l]);
    else lista.push(l);
  }
  const tabelaLgc = {} as PremissasMacro['lgc'];
  for (const r of RATINGS) {
    const linhaRating = {} as Record<Funding, [number, number]>;
    for (const f of FUNDINGS) {
      const escolhida = preferirDaEmpresa(porPar.get(`${r}|${f}`) ?? [], empresaId);
      /* Par ausente vira [0,0] em vez de derrubar a tela: `faixaLGC` não alimenta
         cálculo, e uma tela inteira caída por causa de uma célula seria desproporcional. */
      linhaRating[f] = escolhida ? [escolhida.pct_min, escolhida.pct_max] : [0, 0];
    }
    tabelaLgc[r] = linhaRating;
  }

  /* Perfil é uma SEQUÊNCIA, não um conjunto de células: `sugFin` varre na ordem e para na
     primeira faixa que couber. Por isso a escolha é da LISTA INTEIRA — misturar linhas da
     incorporadora com as da Hype produziria faixas sobrepostas ou buracos. */
  const linhasPerfil = perfil as LinhaPerfil[];
  const daEmpresa = linhasPerfil.filter((l) => l.empresa_id === empresaId);
  const daHype = linhasPerfil.filter((l) => l.empresa_id === null);
  const escolhido = daEmpresa.length > 0 ? daEmpresa : daHype;

  return {
    lgc: tabelaLgc,
    desp: {
      dfMin: d.df_min, dfMax: d.df_max,
      dcMin: d.dc_min, dcMax: d.dc_max,
      segMax: d.seg_max, legMax: d.leg_max,
    },
    perfil: escolhido
      .slice()
      .sort((a, b) => a.ordem - b.ordem)
      .map((l) => ({ ate: l.ate, p: l.pct })),
  };
}

/* =============================================================================
 * Empreendimentos
 * ========================================================================== */

/* Só a identidade. O cadastro (programa, VGV, unidades…) é de cada cenário desde a 0032, e as
   colunas antigas do empreendimento ficaram congeladas (sem SELECT para o app: pedi-las falharia
   a consulta inteira). A contagem de cenários vem EMBUTIDA (PostgREST, mesma ida ao servidor) pela
   FK `cen_emp_fk`, nomeada para não haver ambiguidade. */
const COLS_EMPREENDIMENTO_BASE = 'id, empresa_id, nome, criado_em';
const COLS_EMPREENDIMENTO = `${COLS_EMPREENDIMENTO_BASE}, cenario!cen_emp_fk(id, arquivado_em)`;

type EmpreendimentoBreve = Pick<Linha<'empreendimento'>, 'id' | 'empresa_id' | 'nome' | 'criado_em'> & {
  cenario?: { id: string; arquivado_em: string | null }[] | null;
};

/* Conta só os ATIVOS aqui, no cliente: `cen_sel` mostra o arquivado ao gestor (link antigo), e o
   cartão diz o mesmo que o limite de 5 conta. */
function paraResumoEmpreendimento(e: EmpreendimentoBreve): EmpreendimentoResumo {
  return {
    id: e.id,
    empresaId: e.empresa_id,
    nome: e.nome,
    criadoEm: e.criado_em,
    cenarios: (e.cenario ?? []).filter((c) => c.arquivado_em === null).length,
  };
}

/** Empreendimentos ativos da incorporadora. Arquivado não aparece (política `empd_sel`). */
export async function listarEmpreendimentos(empresaId: string): Promise<EmpreendimentoResumo[]> {
  await registrarAcessoSeForDeFora(empresaId, 'Abriu a lista de empreendimentos');
  const linhas = await exec(
    app().from('empreendimento').select(COLS_EMPREENDIMENTO).eq('empresa_id', empresaId).order('nome') as unknown as
      PromiseLike<{ data: EmpreendimentoBreve[] | null; error: unknown }>,
    'empreendimentos',
  );
  return linhas.map(paraResumoEmpreendimento);
}

/**
 * Cria o empreendimento só com o NOME (0032). O cadastro nasce no 1º cenário: quem cria passa o
 * que foi digitado (se houver) para `criarCenario({ cadastro })`; sem nada, o cenário nasce em branco.
 */
export async function criarEmpreendimento(
  empresaId: string,
  dados: NovoEmpreendimento,
): Promise<EmpreendimentoResumo> {
  const uid = await idDoUsuario('criar empreendimento');
  const linha = await exec(
    app()
      .from('empreendimento')
      .insert({
        empresa_id: empresaId,
        nome: exigirTexto(dados.nome, 'o nome do empreendimento'),
        criado_por: uid,
      })
      .select(COLS_EMPREENDIMENTO_BASE)
      .single(),
    'criar empreendimento',
  );
  return paraResumoEmpreendimento({ ...linha, cenario: [] });   // acabou de nascer: nenhum cenário
}

/** Renomear é a única escrita do app em `app.empreendimento` (fora o arquivamento, que é RPC). */
export async function renomearEmpreendimento(empreendimentoId: string, nome: string): Promise<EmpreendimentoResumo> {
  const linha = await exec(
    app()
      .from('empreendimento')
      .update({ nome: exigirTexto(nome, 'o nome do empreendimento') })
      .eq('id', empreendimentoId)
      .select(COLS_EMPREENDIMENTO)
      .single() as unknown as PromiseLike<{ data: EmpreendimentoBreve | null; error: unknown }>,
    'renomear empreendimento',
  );
  return paraResumoEmpreendimento(linha);
}

/* =============================================================================
 * Cenários
 * ========================================================================== */

const COLS_CENARIO =
  'id, empresa_id, empreendimento_id, nome, tem_fluxo, linha_credito_id, versao, versao_publicada, arquivado_em, criado_em';

type CenarioBreve = Pick<
  Linha<'cenario'>,
  | 'id'
  | 'empresa_id'
  | 'empreendimento_id'
  | 'nome'
  | 'tem_fluxo'
  | 'linha_credito_id'
  | 'versao'
  | 'versao_publicada'
  | 'arquivado_em'
  | 'criado_em'
>;

function paraResumoCenario(c: CenarioBreve): CenarioResumo {
  return {
    id: c.id,
    empresaId: c.empresa_id,
    empreendimentoId: c.empreendimento_id,
    nome: c.nome,
    temFluxo: c.tem_fluxo,
    linhaCreditoId: c.linha_credito_id,
    versao: c.versao,
    versaoPublicada: c.versao_publicada,
    arquivadoEm: c.arquivado_em,
    criadoEm: c.criado_em,
  };
}

/**
 * Os cenários ATIVOS do empreendimento, do mais antigo para o mais novo.
 *
 * O filtro de arquivado é DAQUI, e não da RLS, de propósito: `cen_sel` reabre o arquivado
 * ao gestor (link antigo continua abrindo para ele) — sem o `.is` abaixo, o cenário
 * arquivado voltaria a aparecer no seletor e na comparação, e ocuparia uma "vaga" que o
 * banco já não conta. Os arquivados têm a sua própria porta: `listarCenariosArquivados`.
 */
export async function listarCenarios(empreendimentoId: string): Promise<CenarioResumo[]> {
  const linhas = await exec(
    app()
      .from('cenario')
      .select(COLS_CENARIO)
      .eq('empreendimento_id', empreendimentoId)
      .is('arquivado_em', null)
      .order('criado_em', { ascending: true }),
    'cenarios',
  );
  const primeira: CenarioBreve | undefined = linhas[0];
  /* Desde 09/10/2026 não existe mais "a lista de cenários" como tela: esta leitura alimenta o
     seletor do cenário aberto e a escolha do cenário que abre ao entrar no empreendimento.
     O registro (uma vez por incorporadora e sessão — ver `registrarAcessoSeForDeFora`) diz
     o que aconteceu de fato. */
  if (primeira) await registrarAcessoSeForDeFora(primeira.empresa_id, 'Abriu os cenários de um empreendimento');
  return linhas.map(paraResumoCenario);
}

/** Um cenário arquivado, como o seletor mostra na seção "Arquivados". */
export interface CenarioArquivado {
  id: string;
  nome: string;
  versao: number;
  /** 0 = nunca publicado. Na exclusão definitiva, é o que diz quantas versões vão junto. */
  versaoPublicada: number;
  arquivadoEm: string;
}

/**
 * Os arquivados do empreendimento — gestor, analista e Admin Hype (a RPC recusa Consulta).
 * RPC e não SELECT porque o analista não enxerga arquivado pela RLS, e o Admin Hype também
 * não: quem decide quem lista é a função, numa regra só (0028).
 */
export async function listarCenariosArquivados(empreendimentoId: string): Promise<CenarioArquivado[]> {
  const linhas = await exec(
    app().rpc('cenarios_arquivados', { p_empreendimento: empreendimentoId }),
    'cenários arquivados',
  );
  return linhas.map((l) => ({
    id: l.id,
    nome: l.nome,
    versao: l.versao,
    versaoPublicada: l.versao_publicada,
    arquivadoEm: l.arquivado_em,
  }));
}

/**
 * Arquiva na hora — gestor ou analista, sem pedido nem aprovação (decisão do usuário em
 * 09/10/2026). O arquivado deixa de ocupar uma das 5 vagas e pode ser restaurado.
 */
export async function arquivarCenario(cenarioId: string): Promise<void> {
  await execOpcional(app().rpc('arquivar_cenario', { p_cenario: cenarioId }), 'arquivar cenário');
}

/**
 * Restaura. Volta a ocupar vaga: com 5 ativos o banco recusa (LIMITE_CENARIOS); com outro
 * ativo de mesmo nome, também (renomeie um dos dois).
 */
export async function restaurarCenario(cenarioId: string): Promise<void> {
  await execOpcional(app().rpc('restaurar_cenario', { p_cenario: cenarioId }), 'restaurar cenário');
}

/**
 * Exclui DE VEZ — só o Admin Hype, só cenário arquivado. Apaga o cenário, as filhas, os
 * rascunhos de todo mundo e as versões publicadas; deixa um evento na trilha com o nome.
 * Não há desfazer: a tela pede confirmação dupla antes de chamar.
 */
export async function excluirCenarioDefinitivo(cenarioId: string): Promise<void> {
  await execOpcional(app().rpc('excluir_cenario_definitivo', { p_cenario: cenarioId }), 'excluir cenário');
}

export async function listarLinhasCredito(): Promise<LinhaCredito[]> {
  const linhas = await exec(
    app().from('ref_linha_credito').select('id, nome').eq('ativo', true).order('ordem'),
    'linhas de crédito',
  );
  return linhas.map((l: Pick<Linha<'ref_linha_credito'>, 'id' | 'nome'>) => ({ id: l.id, nome: l.nome }));
}

/**
 * Só a versão do cenário — duas colunas, uma linha, sem trilha de auditoria.
 *
 * É a sonda da sincronização entre pessoas (EL-190). Existe separada de `abrirCenario`
 * por duas razões, e as duas importam: `abrirCenario` faz NOVE consultas (o cenário e as
 * sete filhas, mais a identidade do empreendimento) e, sobretudo, chama
 * `registrarAcessoSeForDeFora` — perguntar "mudou?" a cada 20 segundos gravaria uma
 * entrada de auditoria por pergunta para quem é Admin Hype.
 *
 * Devolve `null` quando não deu para perguntar (rede, sessão): a sonda não é operação do
 * usuário e não pode virar erro na tela. Quem chama trata `null` como "não sei", que é o
 * mesmo que "nada a fazer agora". Desde 09/10/2026 a pergunta respondida SEM a linha vira
 * `sumiu`, e a linha arquivada vira `arquivado` — a tela diz isso em vez de ficar calada.
 */
export async function versaoDoCenario(cenarioId: string): Promise<EstadoDoCenario | null> {
  try {
    const c = await execOpcional(
      app().from('cenario').select('versao, versao_publicada, arquivado_em').eq('id', cenarioId).maybeSingle(),
      'versão do cenário',
    );
    /* A pergunta FOI respondida e a linha não veio: para quem está com o cenário aberto,
       isto é "alguém o tirou daqui" (excluído de vez, ou arquivado — o analista não
       enxerga arquivado). Antes virava `null` ("não sei") e a tela ficava calada. */
    if (c === null || c === undefined) return { situacao: 'sumiu' };
    return {
      situacao: c.arquivado_em === null ? 'ativo' : 'arquivado',
      versao: c.versao,
      versaoPublicada: c.versao_publicada,
    };
  } catch {
    return null;
  }
}

/**
 * O que a sonda sabe do cenário aberto. `arquivado` só chega a quem a RLS ainda deixa ver
 * o arquivado (o gestor); para os demais, arquivar e excluir de vez dão o mesmo `sumiu`.
 */
export type EstadoDoCenario =
  | { situacao: 'ativo' | 'arquivado'; versao: number; versaoPublicada: number }
  | { situacao: 'sumiu' };

/**
 * Abre um cenário no formato que o motor consome.
 *
 * Duas idas ao servidor: (1) o cenário e as 7 filhas em paralelo; (2) a identidade do
 * empreendimento (id e nome), que só é conhecida depois de ler o cenário. O cadastro — e o LGC —
 * vêm da linha do CENÁRIO desde a 0032; a incorporadora não é mais lida. Quem monta
 * `{empreendimento, lev}` é o `mapeamento.ts` — este arquivo não conhece a semântica de nenhum
 * campo do motor.
 *
 * Não carrega o rascunho pessoal de propósito: isso é do `rascunho.ts`, para que "o que
 * está publicado" e "o que eu estava digitando" nunca se confundam na mesma variável.
 *
 * CAL-3 (revisão de 11/10/2026): as 8 consultas não são uma leitura única — se um colega publica
 * no meio, a tela ficaria com a versão NOVA e filhas VELHAS, e a trava otimista deixaria a
 * publicação seguinte passar por cima do trabalho dele. Por isso a versão é lida ANTES e DEPOIS
 * das filhas (`ABRIR_VOLTAS`). Ler só depois não bastaria: no `Promise.all` as filhas podem chegar
 * ANTES da linha do cenário, e filha velha com cenário novo daria "versão igual". A versão só sobe
 * no `salvar_cenario`, na mesma transação das filhas: nada confirmado entre as duas leituras =
 * fotografia consistente. Ainda instável na última volta, vale a versão de ANTES das filhas — o
 * conteúdo é pelo menos tão novo quanto ela, e a próxima publicação bate na trava (recusa segura).
 */
const ABRIR_VOLTAS = 3;

export async function abrirCenario(cenarioId: string): Promise<CenarioAberto> {
  const lerVersao = async (): Promise<number> =>
    (await exec(app().from('cenario').select('versao').eq('id', cenarioId).single(), 'versão do cenário')).versao;

  for (let volta = 1; ; volta++) {
    const antes = await lerVersao();
    const lido = await lerCenarioInteiro(cenarioId);
    const depois = await lerVersao();
    const estavel = antes === depois && depois === lido.cenario.versao;
    if (estavel || volta >= ABRIR_VOLTAS) {
      const aberto = await montarAberto(cenarioId, lido);
      return estavel ? aberto : { ...aberto, cenario: { ...aberto.cenario, versao: antes } };
    }
  }
}

/** As 8 consultas do cenário (a linha e as 7 filhas), em paralelo. */
async function lerCenarioInteiro(cenarioId: string) {
  const [cenario, meses, unidades, vagas, unidadeVaga, obraServico, obraMes, travas] = await Promise.all([
    exec(app().from('cenario').select('*').eq('id', cenarioId).single(), 'cenario'),
    exec(app().from('cenario_mes').select('*').eq('cenario_id', cenarioId).order('ordem'), 'meses'),
    exec(app().from('unidade').select('*').eq('cenario_id', cenarioId).order('lado').order('ordem'), 'unidades'),
    exec(app().from('vaga').select('*').eq('cenario_id', cenarioId).order('lado').order('ordem'), 'vagas'),
    exec(app().from('unidade_vaga').select('*').eq('cenario_id', cenarioId), 'vínculos de vaga'),
    exec(
      app().from('cenario_obra_servico').select('*').eq('cenario_id', cenarioId).order('servico_codigo'),
      'serviços de obra',
    ),
    exec(
      app()
        .from('cenario_obra_mes')
        .select('*')
        .eq('cenario_id', cenarioId)
        .order('servico_codigo')
        .order('offset_mes'),
      'cronograma de obra',
    ),
    exec(app().from('cenario_trava').select('*').eq('cenario_id', cenarioId).order('campo'), 'travas'),
  ]);
  return { cenario, meses, unidades, vagas, unidadeVaga, obraServico, obraMes, travas };
}

/** O cenário lido vira `{cenario, entrada}`: a identidade do empreendimento e o mapeamento. */
async function montarAberto(
  cenarioId: string,
  lido: Awaited<ReturnType<typeof lerCenarioInteiro>>,
): Promise<CenarioAberto> {
  const { cenario, meses, unidades, vagas, unidadeVaga, obraServico, obraMes, travas } = lido;

  /* Pacote novo contra banco SEM a 0032: a linha não traz o cadastro. Seguir montaria o motor com
     cadastro zerado (VGV 0, LGC 0) — número errado e plausível. Falha em voz alta. */
  if (!('lgc_disponivel' in (cenario as object))) {
    throw erro(
      'ERRO_INESPERADO',
      'O servidor ainda não tem o cadastro por cenário (migração 0032). Nada foi alterado — avise o suporte.',
      { operacao: 'abrir cenário', detalhe: 'linha de app.cenario sem lgc_disponivel', erro: null, dados: { cenarioId } },
    );
  }

  await registrarAcessoSeForDeFora(cenario.empresa_id, `Abriu o cenário "${cenario.nome}"`);

  /* Do empreendimento, só a IDENTIDADE (0032): o cadastro e o LGC vêm da linha do cenário, e a
     incorporadora não é mais lida. As colunas antigas do cadastro estão congeladas (sem SELECT). */
  const empreendimento = await exec(
    app().from('empreendimento').select('id, empresa_id, nome, extras').eq('id', cenario.empreendimento_id).single(),
    'empreendimento',
  );

  const linhas: LinhasBanco = {
    empreendimento,
    cenario,
    meses,
    unidades,
    vagas,
    unidadeVaga,
    obraServico,
    obraMes,
    travas,
  };

  return { cenario: paraResumoCenario(cenario), entrada: paraMotor(linhas) };
}

/**
 * Cria um cenário. Sem `cadastro`, ele nasce EM BRANCO (decisão de 12/10/2026): os defaults do
 * banco — SBPE, rating A, zeros, LGC 0 e PJ limite vazio. Com `cadastro` (o 1º cenário de um
 * empreendimento recém-criado, quando a criação tiver campos), grava o que veio.
 * O "Copiar" NÃO passa por aqui: é o `clonar_cenario`, que leva o cadastro da origem.
 */
export async function criarCenario(args: {
  empreendimentoId: string;
  nome: string;
  linhaCreditoId?: number;
  temFluxo?: boolean;
  cadastro?: Partial<CadastroCenario>;
}): Promise<CenarioResumo> {
  const uid = await idDoUsuario('criar cenário');
  const nome = exigirTexto(args.nome, 'o nome do cenário');

  const pai = await exec(
    app().from('empreendimento').select('id, empresa_id').eq('id', args.empreendimentoId).single(),
    'empreendimento',
  );

  let linhaCreditoId = args.linhaCreditoId;
  if (linhaCreditoId === undefined) {
    const opcoes = await listarLinhasCredito();
    const primeira: LinhaCredito | undefined = opcoes[0];
    if (!primeira) {
      throw erro('REGRA_VIOLADA', 'Nenhuma linha de crédito cadastrada.', {
        operacao: 'criar cenário',
        detalhe: 'ref_linha_credito vazia',
        erro: null,
      });
    }
    linhaCreditoId = primeira.id;
  }

  const linha = await exec(
    app()
      .from('cenario')
      .insert({
        ...(definidos({ ...(args.cadastro ?? {}) }) as Partial<CadastroCenario>),
        empresa_id: pai.empresa_id,
        empreendimento_id: pai.id,
        nome,
        tem_fluxo: args.temFluxo ?? true,
        linha_credito_id: linhaCreditoId,
        criado_por: uid,
      })
      .select(COLS_CENARIO)
      .single(),
    'criar cenário',
  );
  return paraResumoCenario(linha);
}

/**
 * Clona um cenário. É a ÚNICA porta de clonagem (R-11): copiar tabela a tabela pelo
 * cliente já produziu clone sem vínculo de vaga — cálculo diferente, e ninguém percebe.
 */
export async function clonarCenario(cenarioOrigemId: string, nome: string): Promise<string> {
  return exec(
    app().rpc('clonar_cenario', { p_origem: cenarioOrigemId, p_nome: exigirTexto(nome, 'o nome do cenário') }),
    'clonar cenário',
  );
}

export async function renomearCenario(cenarioId: string, nome: string): Promise<CenarioResumo> {
  const linha = await exec(
    app()
      .from('cenario')
      .update({ nome: exigirTexto(nome, 'o nome do cenário') })
      .eq('id', cenarioId)
      .select(COLS_CENARIO)
      .single(),
    'renomear cenário',
  );
  return paraResumoCenario(linha);
}

/* =============================================================================
 * PUBLICAR — o botão "Salvar"
 * ========================================================================== */

/** Remove as chaves `undefined`: no contrato da RPC, chave AUSENTE = campo intocado. */
function definidos(o: Record<string, unknown>): Record<string, unknown> {
  const saida: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(o)) if (v !== undefined) saida[k] = v;
  return saida;
}

/**
 * Colunas escalares que `app.salvar_cenario` aceita no bloco `"cenario"`.
 *
 * ⚠ SEM `nome` (09/10/2026). Publicar mandava o nome que a tela tinha em mãos desde a
 * ABERTURA — renomear o cenário aberto (pelo seletor) e depois publicar devolvia o nome
 * antigo, sem aviso nenhum: renomear não sobe a versão, então a trava otimista não via
 * nada. O mesmo acontecia quando OUTRA pessoa renomeava enquanto eu editava. A RPC faz
 * `jsonb_populate_record` sobre a linha ATUAL: chave ausente = nome intocado. Quem muda
 * nome é só `renomearCenario`.
 *
 * COM O CADASTRO (0032): as 13 colunas do cadastro do cenário viajam no MESMO bloco, então o
 * cadastro é gravado na mesma transação e sob a mesma trava otimista do resto do cenário — o
 * CAL-1 (publicar desfazia o cadastro publicado por um colega) deixa de existir por desenho.
 * `lgc_disponivel` é obrigatório no bloco: sem ele, a trava PACOTE_DESATUALIZADO do banco recusa.
 */
const CAMPOS_CENARIO = [
  'tem_fluxo', 'linha_credito_id', 'pct_fin', 'unid_vend_dem', 'unid_fin_dem',
  'aporte', 'data_aporte', 'terreno_garantia', 'obra_gar_pct', 'prazo_obra', 'mes_inicial',
  'antecip_pct', 'data_demanda_desejada', 'data_contratacao', 'lgc_pct', 'lgc_limite_desejado',
  'pj_limitado_lgc', 'min_obra_pct', 'tem_evv', 'funding_obra', 'pls0_pct', 'pls0_dias_aprov',
  'extensao_demanda', 'extensao_autorizada_por', 'extensao_autorizada_em', 'custo_real_obra',
  'data_medicao_igh', 'tempo_registro', 'objetivo_guiado', 'extras',
  /* o cadastro do cenário (0032) */
  'programa', 'rating', 'vgv', 'unidades', 'cto_laudo', 'desp_fin', 'desp_com', 'desp_seg', 'desp_leg',
  'valor_terreno', 'permutadas', 'lgc_disponivel', 'pj_limite',
  /* os juros do saldo devedor do PJ (0034, Fluxo pelo memorial). Num banco ANTES da 0034 o
     jsonb_populate_record do salvar_cenario ignora as chaves — os juros só não são salvos */
  'juros_indexador_taxa', 'juros_pct_indexador', 'juros_taxa_nominal_aa', 'juros_indexador_saldo',
  /* amortização e carência do PJ (0034, 15/10/2026) */
  'amort_inicio_meses', 'amort_parcelas', 'carencia_ciclo_meses', 'carencia_adicional_1', 'carencia_adicional_2', 'carencia_adicional_3',
] as const;

/**
 * Traduz as linhas do `mapeamento.ts` para o jsonb que `app.salvar_cenario` espera
 * (contrato no cabeçalho de `db/migrations/0010_rpc.sql`).
 *
 * A diferença que importa: no banco o vínculo unidade↔vaga é por UUID; na RPC é por
 * `(lado, unidade_ordem, vaga_ordem)`. É assim de propósito — a RPC recria as linhas
 * e os UUIDs de `paraBanco` são novos a cada chamada; `ordem` é a identidade estável.
 */
function conteudoParaRpc(linhas: LinhasBanco): Json {
  const ordemDaUnidade = new Map(linhas.unidades.map((u) => [u.id, u.ordem]));
  const ordemDaVaga = new Map(linhas.vagas.map((v) => [v.id, v.ordem]));

  const cenario: Record<string, unknown> = {};
  const origem = linhas.cenario as unknown as Record<string, unknown>;
  for (const campo of CAMPOS_CENARIO) if (origem[campo] !== undefined) cenario[campo] = origem[campo];

  const conteudo = {
    cenario,
    meses: linhas.meses.map((m) =>
      definidos({
        ordem: m.ordem,
        ym: m.ym,
        vend_fin: m.vend_fin,
        vend_nao: m.vend_nao,
        obra_pct: m.obra_pct,
        obra_custo_pct: m.obra_custo_pct,
      }),
    ),
    unidades: linhas.unidades.map((u) =>
      definidos({
        lado: u.lado,
        ordem: u.ordem,
        nome: u.nome,
        valor_venda: u.valor_venda,
        avaliacao: u.avaliacao,
        data_venda: u.data_venda,
        data_registro: u.data_registro,
        financiada: u.financiada,
        perc_fin: u.perc_fin,
        permutada: u.permutada,
        perm_inf_banco: u.perm_inf_banco,
        perm_como_venda: u.perm_como_venda,
        fracao_ideal: u.fracao_ideal,
        ingressa_demanda: u.ingressa_demanda,
        valor_solic_aval: u.valor_solic_aval,
        data_avaliacao: u.data_avaliacao,
        forcar_avaliacao: u.forcar_avaliacao,
      }),
    ),
    vagas: linhas.vagas.map((v) =>
      definidos({
        lado: v.lado,
        ordem: v.ordem,
        descricao: v.descricao,
        tipo_id: v.tipo_id,
        valor_venda: v.valor_venda,
        avaliacao: v.avaliacao,
        fracao: v.fracao,
        permutada: v.permutada,
        perm_inf_banco: v.perm_inf_banco,
      }),
    ),
    unidade_vaga: linhas.unidadeVaga
      .map((uv) => ({
        lado: uv.lado,
        unidade_ordem: ordemDaUnidade.get(uv.unidade_id),
        vaga_ordem: ordemDaVaga.get(uv.vaga_id),
      }))
      // vínculo cuja ponta sumiu não vai: a RPC recusaria a linha inteira.
      .filter((uv) => uv.unidade_ordem !== undefined && uv.vaga_ordem !== undefined),
    obra_servico: linhas.obraServico.map((s) => ({
      servico_codigo: s.servico_codigo,
      pls_pct: s.pls_pct,
      custo_pct: s.custo_pct,
    })),
    obra_mes: linhas.obraMes.map((o) => ({
      servico_codigo: o.servico_codigo,
      offset_mes: o.offset_mes,
      pct: o.pct,
    })),
    travas: linhas.travas.map((t) => t.campo),
  };

  return conteudo as unknown as Json;
}

/**
 * `salvar_cenario` devolve o MESMO `CONFLITO_VERSAO` para três situações (0010: "nao esta
 * na versao % (ou arquivado / sem permissao de escrita)"). A tela traduzia todas como
 * "alguém publicou enquanto você editava" — falso quando o cenário foi ARQUIVADO por um
 * colega, e o usuário ia reabrir à procura de uma versão nova que não existe.
 *
 * Desde 09/10/2026 arquivar está a um clique no seletor, então a pergunta vale a ida ao
 * servidor: uma leitura de uma coluna, só no caminho do erro. Na dúvida, fica o erro original.
 */
async function explicarConflito(e: unknown, cenarioId: string): Promise<unknown> {
  if (!(e instanceof ErroFincast) || e.codigo !== 'CONFLITO_VERSAO') return e;
  try {
    const c = await execOpcional(
      app().from('cenario').select('arquivado_em').eq('id', cenarioId).maybeSingle(),
      'cenário',
    );
    if (c === null || c === undefined) {
      return erro(
        'CENARIO_INEXISTENTE',
        'Este cenário foi arquivado ou excluído por outra pessoa e não aceita mais publicação. Escolha outro cenário no seletor do alto da página.',
        { operacao: 'publicar', detalhe: 'linha invisível depois do conflito', erro: e },
      );
    }
    if (c.arquivado_em !== null) {
      return erro(
        'CENARIO_ARQUIVADO',
        'Este cenário foi arquivado por alguém da equipe e não aceita mais publicação. O que você digitou continua guardado: restaure o cenário em "Arquivados", no seletor do alto da página, para publicar.',
        { operacao: 'publicar', detalhe: 'arquivado depois da abertura', erro: e },
      );
    }
  } catch {
    /* sem como perguntar: fica o conflito como veio */
  }
  return e;
}

/**
 * Publica o cenário: grava `app.cenario` + filhas e cria a versão em `app.cenario_versao`.
 *
 * É o que o botão "Salvar" faz — e a ÚNICA escrita no cenário publicado em todo o
 * produto. O digitar do usuário nunca chega aqui; ele vive em `app.cenario_rascunho`.
 *
 * `cenario.versao` é a trava otimista: tem de ser a versão que veio de `abrirCenario()`.
 * Se outra pessoa publicou no meio, o banco recusa com `CONFLITO_VERSAO` em vez de
 * sobrescrever o trabalho dela.
 *
 * O CADASTRO vai junto, no MESMO `salvar_cenario` (0032): é do cenário, e publicar um cenário não
 * toca outro cenário, o empreendimento nem a incorporadora. Antes da ida, o cadastro é conferido
 * (`problemasDoCadastro`): um valor que o banco recusaria reprovaria a publicação inteira.
 *
 * Depois de publicar, o rascunho pessoal ficou velho: chame `descartarRascunho()`.
 */
export async function publicarCenario(args: {
  /** O cenário como veio de `abrirCenario()` — id, empresa, nome e a versão-base. */
  cenario: CenarioResumo;
  /** `{empreendimento, lev}` do motor, no estado que se quer publicar. */
  entrada: EntradaMotor;
  rotulo?: string;
  motivo?: string;
  /** Exigidos pelo CHECK `cen_extensao_autor` quando a extensão de demanda é > 0. */
  extensaoAutorizadaPor?: string | null;
  extensaoAutorizadaEm?: string | null;
}): Promise<ResultadoPublicacao> {
  const { cenario } = args;
  const opcoes: OpcoesParaBanco = {
    empresaId: cenario.empresaId,
    cenarioId: cenario.id,
    /* Só para a linha ficar completa (a coluna é NOT NULL): o nome NÃO viaja para a RPC —
       `CAMPOS_CENARIO` o deixa de fora, e é lá que mora a regra. */
    nomeCenario: cenario.nome,
    temFluxo: cenario.temFluxo,
    extensaoAutorizadaPor: args.extensaoAutorizadaPor,
    extensaoAutorizadaEm: args.extensaoAutorizadaEm,
  };
  const linhas = paraBanco(args.entrada, opcoes);

  const problemas = problemasDoCadastro(linhas.cenario as unknown as Record<string, unknown>);
  if (problemas.length > 0) {
    throw erro(
      'CADASTRO_INVALIDO',
      `Revise o cadastro antes de publicar: ${problemas.join('; ')}. Nada foi publicado — o que você digitou está guardado.`,
      { operacao: 'publicar', detalhe: problemas.join(' | '), erro: null, dados: { cenarioId: cenario.id } },
    );
  }

  return conexao.rastrearSalvamento(async () => {
    /* Etapa 1 — atômica: cenário + 7 filhas + o CADASTRO do cenário (0032), com trava otimista.
       Até a 0032 o cadastro era do EMPREENDIMENTO, compartilhado, e ia numa etapa à parte (1b),
       por UPDATE sem trava de versão: a tela velha de uma pessoa desfazia o cadastro que outra
       tinha publicado (CAL-1), e uma falha ali deixava "cenário salvo, cadastro não". Agora tudo
       vai nesta chamada, numa transação só. */
    let versao: number;
    try {
      versao = await exec(
        app().rpc('salvar_cenario', {
          p_cenario_id: cenario.id,
          p_versao: cenario.versao,
          p_conteudo: conteudoParaRpc(linhas),
        }),
        'salvar cenário',
      );
    } catch (e) {
      throw await explicarConflito(e, cenario.id);
    }

    // Etapa 2 — atômica: snapshot + cenario_versao + bump de versao_publicada.
    try {
      const publicada = await registrarVersao(cenario.id, args.rotulo, args.motivo);
      return {
        versao,
        versaoPublicada: publicada.versao,
        versaoId: publicada.id,
        criadoEm: publicada.criadoEm,
      };
    } catch (e) {
      const causa = traduzirErro(e, 'publicar versão');
      throw erro(
        'VERSAO_NAO_REGISTRADA',
        'Os dados foram salvos, mas a versão não chegou a ser registrada. Tente publicar de novo — não refaça o salvamento.',
        {
          operacao: 'publicar versão',
          detalhe: causa.message,
          erro: e,
          dados: { versaoSalva: versao, cenarioId: cenario.id, codigoOriginal: causa.codigo },
        },
      );
    }
  });
}

/**
 * Só a etapa 2: tira o snapshot do que já está gravado e registra a versão. É a
 * correção de `VERSAO_NAO_REGISTRADA` — e o "publicar sem ter alterado nada".
 */
export async function registrarVersao(
  cenarioId: string,
  rotulo?: string,
  motivo?: string,
): Promise<{ id: string; versao: number; criadoEm: string }> {
  const linhas = await exec(
    app().rpc('publicar_versao', { p_cenario_id: cenarioId, p_rotulo: rotulo ?? '', p_motivo: motivo ?? '' }),
    'publicar versão',
  );
  const primeira: { id: string; versao: number; criado_em: string } | undefined = linhas[0];
  if (!primeira) {
    throw erro('ERRO_INESPERADO', 'O servidor não confirmou a publicação da versão.', {
      operacao: 'publicar versão',
      detalhe: 'publicar_versao devolveu 0 linhas',
      erro: null,
    });
  }
  return { id: primeira.id, versao: primeira.versao, criadoEm: primeira.criado_em };
}

/* =============================================================================
 * Convites e membros
 * ========================================================================== */

function hex(bytes: Uint8Array): string {
  let s = '';
  for (const b of bytes) s += b.toString(16).padStart(2, '0');
  return s;
}

/** Só o que usamos da Web Crypto — evita depender da lib "DOM" no tsconfig. */
interface CriptoMinimo {
  getRandomValues<T extends ArrayBufferView>(alvo: T): T;
  subtle: { digest(algoritmo: string, dados: ArrayBufferView): Promise<ArrayBuffer> };
}

/**
 * Gera o par (token em claro, literal `\x…` para a coluna `bytea`).
 *
 * O banco compara com `extensions.digest(p_token,'sha256')`, isto é, o sha256 dos bytes
 * do token como TEXTO — daí o `TextEncoder` sobre a string. Token em hex (256 bits) para
 * atravessar URL de e-mail sem escape.
 */
async function gerarToken(): Promise<{ token: string; tokenHash: string }> {
  const cripto = (globalThis as unknown as { crypto?: CriptoMinimo }).crypto;
  if (!cripto || typeof cripto.getRandomValues !== 'function' || !cripto.subtle) {
    throw erro('ERRO_INESPERADO', 'Este navegador não oferece geração segura de token de convite.', {
      operacao: 'convite',
      detalhe: 'Web Crypto ausente',
      erro: null,
    });
  }
  const token = hex(cripto.getRandomValues(new Uint8Array(32)));
  const digest = await cripto.subtle.digest('SHA-256', new TextEncoder().encode(token));
  return { token, tokenHash: `\\x${hex(new Uint8Array(digest))}` };
}

const COLS_CONVITE = 'id, empresa_id, email, papel, expira_em, criado_em, criado_por';

type ConviteBreve = Pick<
  Linha<'convite'>,
  'id' | 'empresa_id' | 'email' | 'papel' | 'expira_em' | 'criado_em' | 'criado_por'
>;

function paraConvite(c: ConviteBreve): ConvitePendente {
  return {
    id: c.id,
    empresaId: c.empresa_id,
    email: c.email,
    papel: c.papel,
    expiraEm: c.expira_em,
    criadoEm: c.criado_em,
    criadoPor: c.criado_por,
  };
}

/**
 * Emite um convite. Só gestor da incorporadora ou Admin Hype consegue (política
 * `convite_ins`, que ainda exige `criado_por = auth.uid()`).
 *
 * Ninguém se cadastra sozinho: o gatilho `t_barrar_signup` em `auth.users` recusa
 * qualquer signup sem convite pendente. Quem manda o e-mail é a camada de aplicação —
 * esta função devolve o token uma vez e nunca mais; o banco guarda só o hash.
 */
export async function convidarUsuario(args: {
  empresaId: string;
  email: string;
  papel: Papel;
  diasValidade?: number;
}): Promise<ConviteEmitido> {
  const uid = await idDoUsuario('convidar usuário');
  const email = exigirTexto(args.email, 'o e-mail do convidado').toLowerCase();
  if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
    throw erro('DADO_INVALIDO', 'E-mail inválido.', { operacao: 'convite', detalhe: 'formato', erro: null });
  }

  const { token, tokenHash } = await gerarToken();
  const dias = args.diasValidade ?? 7;

  const linha = await exec(
    app()
      .from('convite')
      .insert({
        empresa_id: args.empresaId,
        email,
        papel: args.papel,
        token_hash: tokenHash,
        expira_em: new Date(Date.now() + dias * 86_400_000).toISOString(),
        criado_por: uid,
      })
      .select(COLS_CONVITE)
      .single(),
    'convidar usuário',
  );

  return { convite: paraConvite(linha), token };
}

/** Convites ainda abertos: nem aceitos, nem revogados, nem vencidos. */
export async function listarConvites(empresaId: string): Promise<ConvitePendente[]> {
  // Convites pendentes expõem e-mails de pessoas do cliente — mesmo motivo de listarMembros.
  await registrarAcessoSeForDeFora(empresaId, 'Abriu os convites pendentes');
  const linhas = await exec(
    app()
      .from('convite')
      .select(COLS_CONVITE)
      .eq('empresa_id', empresaId)
      .is('aceito_em', null)
      .is('revogado_em', null)
      .gt('expira_em', new Date().toISOString())
      .order('criado_em', { ascending: false }),
    'convites',
  );
  return linhas.map(paraConvite);
}

/**
 * Revoga um convite pendente. Convite já aceito não é revogável (a política exige
 * `aceito_em is null`) — para tirar quem já entrou, o que muda é `membro_empresa`.
 */
export async function revogarConvite(conviteId: string): Promise<void> {
  const uid = await idDoUsuario('revogar convite');
  const linhas = await exec(
    app()
      .from('convite')
      .update({ revogado_em: new Date().toISOString(), revogado_por: uid })
      .eq('id', conviteId)
      .is('aceito_em', null)
      .is('revogado_em', null)
      .select('id'),
    'revogar convite',
  );
  if (linhas.length === 0) {
    throw erro('NAO_ENCONTRADO', 'Convite não encontrado, já aceito ou já revogado.', {
      operacao: 'revogar convite',
      detalhe: '0 linhas afetadas',
      erro: null,
      dados: { conviteId },
    });
  }
}

interface LinhaMembro {
  papel: Papel;
  ativo: boolean;
  criado_em: string;
  usuario: { id: string; nome: string; email: string; ativo: boolean } | null;
}

/** Membros da incorporadora, gestores primeiro. */
/**
 * Troca o papel de alguém na incorporadora.
 *
 * Duas guardas vivem no banco e NÃO são repetidas aqui, de propósito — repetir regra é
 * criar duas verdades que divergem com o tempo:
 *   · `membro_upd` exige `usuario_id <> auth.uid()`: ninguém muda o próprio papel. Um
 *     gestor que pudesse se rebaixar deixaria a incorporadora sem administrador por
 *     descuido, e sem ninguém que pudesse desfazer.
 *   · `t_gestor` recusa a operação que deixaria a incorporadora **sem gestor ativo**.
 *     O erro chega traduzido como `SEM_GESTOR`.
 */
export async function alterarPapelMembro(
  empresaId: string,
  usuarioId: string,
  papel: Papel,
): Promise<void> {
  await exec(
    app()
      .from('membro_empresa')
      .update({ papel })
      .eq('empresa_id', empresaId)
      .eq('usuario_id', usuarioId)
      .select('usuario_id')
      .single(),
    'alterar papel',
  );
}

/**
 * Liga ou desliga o acesso de alguém, **sem apagar nada**.
 *
 * Desativar em vez de remover é decisão de produto: o histórico aponta para o usuário
 * (`criado_por` em empreendimento, cenário e versão publicada), e apagar a linha deixaria
 * a trilha de auditoria falando de um id que ninguém mais consegue identificar. Desligado,
 * a pessoa some de `emp_leitura()` e não enxerga mais nada — o efeito prático é o mesmo,
 * e o passado continua legível.
 */
export async function definirMembroAtivo(
  empresaId: string,
  usuarioId: string,
  ativo: boolean,
): Promise<void> {
  await exec(
    app()
      .from('membro_empresa')
      .update({ ativo })
      .eq('empresa_id', empresaId)
      .eq('usuario_id', usuarioId)
      .select('usuario_id')
      .single(),
    ativo ? 'reativar acesso' : 'desativar acesso',
  );
}

export async function listarMembros(empresaId: string): Promise<Membro[]> {
  /* Nome e e-mail das pessoas da incorporadora são dado do cliente: o acesso de fora
     tem de deixar rastro. Antes, esta função e `listarConvites` não registravam em
     nenhuma hipótese — e são justamente as que expõem pessoas, não números. */
  await registrarAcessoSeForDeFora(empresaId, 'Abriu a lista de membros');
  // `usuario:usuario_id` explícito: membro_empresa tem DUAS FKs para usuario (a outra é
  // criado_por) e, sem desambiguar, o PostgREST devolve erro de relacionamento ambíguo.
  // O cast existe porque a inferência do embed varia entre versões do postgrest-js.
  const consulta = app()
    .from('membro_empresa')
    .select('papel, ativo, criado_em, usuario:usuario_id ( id, nome, email, ativo )')
    .eq('empresa_id', empresaId) as unknown as PromiseLike<{ data: LinhaMembro[] | null; error: unknown }>;

  const linhas = await exec(consulta, 'membros');

  return linhas
    .filter((m): m is LinhaMembro & { usuario: NonNullable<LinhaMembro['usuario']> } => m.usuario !== null)
    .map((m) => ({
      usuarioId: m.usuario.id,
      nome: m.usuario.nome,
      email: m.usuario.email,
      papel: m.papel,
      ativo: m.ativo && m.usuario.ativo,
      desde: m.criado_em,
    }))
    .sort((a, b) => PAPEIS.indexOf(a.papel) - PAPEIS.indexOf(b.papel) || a.nome.localeCompare(b.nome, 'pt-BR'));
}

/* =============================================================================
 * Pedidos de exclusão (migration 0021)
 *
 * "Excluir" é ARQUIVAR, e é reversível. Quem pede é gestor ou analista (para a
 * incorporadora inteira, só gestor); quem decide é o Admin Hype, e a decisão passa
 * por RPC `security definer` — a RLS não tem política de UPDATE para aprovar, de
 * propósito. Nada aqui repete a regra de permissão: quem barra é o banco.
 * ========================================================================== */

const COLS_PEDIDO =
  'id, empresa_id, alvo, empreendimento_id, alvo_nome, motivo, estado, solicitante_id, ' +
  'solicitado_em, decidido_em, decisao_nota, solicitante:solicitante_id ( nome )';

interface LinhaPedido {
  id: string;
  empresa_id: string;
  alvo: AlvoExclusao;
  empreendimento_id: string | null;
  alvo_nome: string;
  motivo: string;
  estado: EstadoPedido;
  solicitante_id: string;
  solicitado_em: string;
  decidido_em: string | null;
  decisao_nota: string;
  solicitante: { nome: string } | null;
}

function paraPedido(l: LinhaPedido): PedidoExclusao {
  return {
    id: l.id,
    empresaId: l.empresa_id,
    alvo: l.alvo,
    empreendimentoId: l.empreendimento_id,
    alvoNome: l.alvo_nome,
    motivo: l.motivo,
    estado: l.estado,
    solicitanteId: l.solicitante_id,
    /* Nome vazio, e não "—": quem monta a tela decide como mostrar ausência. */
    solicitanteNome: l.solicitante?.nome ?? '',
    solicitadoEm: l.solicitado_em,
    decididoEm: l.decidido_em,
    decisaoNota: l.decisao_nota,
  };
}

/**
 * Pede a exclusão de um empreendimento ou de uma incorporadora inteira.
 * Devolve o id do pedido criado.
 */
export async function pedirExclusao(args: {
  alvo: AlvoExclusao;
  empresaId: string;
  empreendimentoId?: string | null;
  motivo?: string;
}): Promise<string> {
  return exec(
    app().rpc('pedir_exclusao', {
      p_alvo: args.alvo,
      p_empresa: args.empresaId,
      /* Ausente = o `default null` da 0021 (pedido da incorporadora inteira). Os tipos gerados do
         banco (B7, 12/10/2026) declaram o argumento opcional, sem null. */
      p_empreendimento: args.empreendimentoId ?? undefined,
      p_motivo: args.motivo ?? '',
    }),
    'pedir exclusão',
  );
}

/**
 * Os pedidos que a pessoa enxerga. Sem `empresaId`, vem tudo o que a RLS deixa —
 * que para o Admin Hype é a plataforma inteira, e é isso que alimenta a fila dele.
 */
export async function listarPedidosExclusao(opcoes: {
  empresaId?: string;
  apenasPendentes?: boolean;
} = {}): Promise<PedidoExclusao[]> {
  let consulta = app().from('pedido_exclusao').select(COLS_PEDIDO);
  if (opcoes.empresaId !== undefined) consulta = consulta.eq('empresa_id', opcoes.empresaId);
  if (opcoes.apenasPendentes === true) consulta = consulta.eq('estado', 'pendente');
  const linhas = await exec(
    consulta.order('solicitado_em', { ascending: false }) as unknown as PromiseLike<{
      data: LinhaPedido[] | null;
      error: unknown;
    }>,
    'pedidos de exclusão',
  );
  return linhas.map(paraPedido);
}

/** O que a recontagem da barra lateral lê de cada pedido pendente (EL-185). */
export interface PedidoPendente {
  id: string;
  solicitadoEm: string;
}

/**
 * Os pedidos AGUARDANDO DECISÃO, só id e data — para o número do item "Pedidos" da barra
 * lateral, que a casca reconta a cada troca de tela, a cada minuto e ao voltar para a aba.
 *
 * Por que não `listarPedidosExclusao({ apenasPendentes: true })`: aquela traz todas as
 * colunas mais o nome de quem pediu (um join), e roda aqui a sessão inteira. Esta é coberta
 * pelo índice parcial `pedido_exclusao_fila_idx` (0021).
 * Por que não uma contagem `head`: com `head` o `data` vem nulo e `exec` o trata como
 * NAO_ENCONTRADO; e só o total não percebe "um decidido + um novo" entre duas leituras.
 *
 * Passa por `exec` como toda leitura: uma falha de rede aqui também põe "Sem conexão" na
 * barra superior — agora em qualquer tela do Admin Hype, e não só dentro do cenário. É
 * coerente (a rede caiu de fato) e foi declarado no lote de 09/10/2026.
 */
export async function listarPendentesExclusao(): Promise<PedidoPendente[]> {
  const linhas = await exec(
    app().from('pedido_exclusao').select('id, solicitado_em').eq('estado', 'pendente') as unknown as PromiseLike<{
      data: { id: string; solicitado_em: string }[] | null;
      error: unknown;
    }>,
    'pedidos aguardando decisão',
  );
  return linhas.map((l) => ({ id: l.id, solicitadoEm: l.solicitado_em }));
}

/** Aprova (arquiva) ou recusa um pedido. Só Admin Hype — a RPC checa na 1ª linha. */
export async function decidirExclusao(pedidoId: string, aprovar: boolean, nota = ''): Promise<EstadoPedido> {
  return exec(
    app().rpc('decidir_exclusao', { p_pedido: pedidoId, p_aprovar: aprovar, p_nota: nota }),
    aprovar ? 'aprovar exclusão' : 'recusar exclusão',
  );
}

/**
 * Devolve à vida o que um pedido aprovado arquivou — inclusive os cenários que foram
 * junto, que a própria linha do pedido guarda. É por isso que arquivar é reversível.
 */
export async function desfazerExclusao(pedidoId: string, nota = ''): Promise<void> {
  await execOpcional(app().rpc('desfazer_exclusao', { p_pedido: pedidoId, p_nota: nota }), 'desfazer exclusão');
}
